import { getAppCfg } from "../../core/app-cfg";
import { projectListId } from "../../core/code/loader";
import { EDITOR_VIEW_NAME, LAUNCH_VIEW_NAME } from "../../core/constants";
import { cloudProjectId } from "../../core/project/projectid";
import { getIconForProject } from "../../core/project/projectslist";
import { getDomainCfg } from "../../core/state";
import { LocalStorageData, ProjectData } from "../../core/types";
import { getViewData, getViewState } from "../../core/views";
import { compressAsBase64 } from "../zipping";
import { getUserDataKey, LS_VERSION } from "./keys";
import { getLocalStorageProjectList } from "./loader";

const saveUserState = () : LocalStorageData => {
  const editorState = getViewData(EDITOR_VIEW_NAME);

  // TODO add version, add project data if template project

  const localStorageData : LocalStorageData = {
    version : LS_VERSION,
    userId : '', // TODO how do we get the userId? Maybe check in result code....
    code : editorState.data?.editorCode,
    sources : editorState.data?.editorTabs,
    assets : editorState.data?.userAssets,
    projectProgress : editorState.data?.projectProgress,
    projectFinished : editorState.data?.projectFinished,
    projectName : editorState.project?.project,
    projectHint : editorState.project?.hint,
    projectTask : editorState.project?.task,
    chatMessages: editorState.data?.chatMessages || [],
    agentChatMessages: editorState.data?.agentChatMessages || [],
    agentChatId: editorState.data?.agentChatId,
    lastUsedModelId: editorState.data?.lastUsedModelId
  };

  return localStorageData;
}

export const saveUserCodeToLS = () : void => {
  
  const domainCfg = getDomainCfg();
  if (domainCfg.viewSettings.features.localStorageAvailable === false) {
    return;
  }

  if (getViewState().currentView === LAUNCH_VIEW_NAME) {
    return;
  }

  const localStorageData = saveUserState();
  const skJson = JSON.stringify(localStorageData);
  if (domainCfg.viewSettings.features.compressData) {
    compressAsBase64(skJson, async (compressed) => {
      localStorage.setItem(getUserDataKey(undefined), compressed);
      localStorage.setItem(await cloudProjectId(undefined), compressed);
    });
  } else {
    localStorage.setItem(getUserDataKey(undefined), skJson);
  }

}

/**
 * Speichert nur Meta-Daten für V2-Projekte ins LocalStorage.
 * Dateien (code/sources) werden NICHT gespeichert - die liegen im VFS.
 */
export const saveV2MetaToLS = () : void => {
  const domainCfg = getDomainCfg();
  if (domainCfg.viewSettings.features.localStorageAvailable === false) {
    return;
  }

  if (getViewState().currentView === LAUNCH_VIEW_NAME) {
    return;
  }

  const editorState = getViewData(EDITOR_VIEW_NAME);

  // Nur Meta-Daten, KEINE Datei-Inhalte (code/sources/editorTabs)
  // ABER: fileDefinitions (Pfade/Namen) müssen gespeichert werden für hochgeladene Dateien
  const currentTabs = editorState.data?.projectContent?.tabs || [];
  const fileDefinitions = currentTabs.map((tab: any) => ({
    path: tab.path,
    name: tab.name,
    language: tab.language,
    active: tab.active,
    replaceKey: tab.replaceKey,
    readOnly: tab.readOnly
  }));
  
  const v2MetaData = {
    version : LS_VERSION,
    isV2: true, // Marker für V2-Projekt
    userId : '',
    // KEINE code, sources, editorTabs! (Inhalte liegen im VFS)
    // ABER: fileDefinitions für hochgeladene Dateien
    fileDefinitions: fileDefinitions,
    assets : editorState.data?.userAssets,
    projectProgress : editorState.data?.projectProgress,
    projectFinished : editorState.data?.projectFinished,
    projectName : editorState.project?.project,
    projectHint : editorState.project?.hint,
    projectTask : editorState.project?.task,
    chatMessages: editorState.data?.chatMessages || [],
    agentChatMessages: editorState.data?.agentChatMessages || [],
    agentChatId: editorState.data?.agentChatId,
    lastUsedModelId: editorState.data?.lastUsedModelId,
    /** Zeitstempel letzter VFS-Schreibzugriff – für Cloud vs. lokales VFS beim Reload */
    lastVfsWriteAt: Date.now(),
  };

  const skJson = JSON.stringify(v2MetaData);
  if (domainCfg.viewSettings.features.compressData) {
    compressAsBase64(skJson, async (compressed) => {
      localStorage.setItem(getUserDataKey(undefined), compressed);
      localStorage.setItem(await cloudProjectId(undefined), compressed);
    });
  } else {
    localStorage.setItem(getUserDataKey(undefined), skJson);
  }
}

export const saveProjectInLocalProjectList = (projectData) => {
  const viewData = getViewData(EDITOR_VIEW_NAME);
  let projectList = getLocalStorageProjectList();

  const listEntry = {
        id : getAppCfg().projectId,
        name : projectData.project.project,
        language: projectData.project.language,
        screenshot:null,
        icon: getIconForProject(getAppCfg().projectId),
        status:"private"
  }

  var index = projectList.findIndex(function(item) {
    return item.id === listEntry.id;
  });
  
  // Wenn der Eintrag bereits vorhanden ist, aktualisieren
  if (index !== -1) {
    projectList[index] = listEntry;
  } else {
    // Wenn der Eintrag nicht vorhanden ist, hinzufügen
    projectList.push(listEntry);
  }

  saveLocalStorageProjectList(projectList);
}

function saveLocalStorageProjectList(projectList) {
  localStorage.setItem(projectListId(), JSON.stringify(projectList));
}

export const removeProjectFromLocalStorageProjectList = (projectId: string) => {
  let projectList = getLocalStorageProjectList();
  
  // Filter out the project to remove
  projectList = projectList.filter((item) => item.id !== projectId);
  
  saveLocalStorageProjectList(projectList);
}